import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2, CheckCircle, AlertCircle } from "lucide-react";

export default function PublicReviewSubmissionPage() {
  const { token } = useParams<{ token: string }>();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Validate token using Convex query
  const validationResult = useQuery(api.publicReviews.validateToken, token ? { token } : "skip");

  const submitReview = useMutation(api.publicReviews.submitPublicReview);

  // Pre-fill email if available from validation
  useEffect(() => {
    if (validationResult?.reviewRequest?.recipientEmail) {
      setClientEmail(validationResult.reviewRequest.recipientEmail);
    }
    if (validationResult?.reviewRequest?.recipientName) {
      setClientName(validationResult.reviewRequest.recipientName);
    }
  }, [validationResult]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setError("");
    setIsSubmitting(true);

    try {
      // Get client IP and user agent for rate limiting
      let clientInfo = { ip: "unknown", userAgent: "unknown" };
      try {
        const ipResponse = await fetch("/api/client-info");
        if (ipResponse.ok) {
          clientInfo = await ipResponse.json();
        }
      } catch (err) {
        console.warn("Could not get client info:", err);
        // Use browser info as fallback
        clientInfo = {
          ip: "unknown",
          userAgent: navigator.userAgent || "unknown",
        };
      }

      // Format local date as YYYY-MM-DD
      const now = new Date();
      const clientDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

      const result = await submitReview({
        token,
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        rating,
        reviewText: reviewText.trim(),
        ipAddress: clientInfo.ip,
        userAgent: clientInfo.userAgent,
        clientDate: clientDate,
      });

      if (result.success) {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    rating > 0 && clientName.trim() && clientEmail.trim() && reviewText.trim().length >= 10;

  // Loading state
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
              <h1 className="mb-2 text-xl font-semibold text-gray-900">Invalid Review Link</h1>
              <p className="text-gray-600">No token provided in the URL.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading validation
  if (validationResult === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-500" />
              <h1 className="mb-2 text-xl font-semibold text-gray-900">Loading...</h1>
              <p className="text-gray-600">Validating your review link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />
              <h1 className="mb-2 text-xl font-semibold text-gray-900">Thank You!</h1>
              <p className="mb-4 text-gray-600">
                Your review has been submitted successfully. We appreciate your feedback!
              </p>
              <div className="text-sm text-gray-500">
                <p>Business: {validationResult.business?.name}</p>
                <p>
                  Rating: {rating} star{rating !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error states
  if (!validationResult.valid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
              <h1 className="mb-2 text-xl font-semibold text-gray-900">
                {validationResult.alreadySubmitted
                  ? "Review Already Submitted"
                  : "Invalid Review Link"}
              </h1>
              <p className="mb-4 text-gray-600">{validationResult.error}</p>
              {validationResult.alreadySubmitted && (
                <p className="text-sm text-gray-500">Thank you for your previous review!</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main review form
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          {validationResult.business?.logo && (
            <img
              src={validationResult.business.logo}
              alt={validationResult.business.name}
              className="mx-auto mb-4 h-16"
            />
          )}
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Leave a Review</h1>
          <p className="text-lg text-gray-600">for {validationResult.business?.name}</p>
        </div>

        {/* Custom message */}
        {validationResult.reviewRequest?.message && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <p className="italic text-gray-700">"{validationResult.reviewRequest.message}"</p>
            </CardContent>
          </Card>
        )}

        {/* Review form */}
        <Card>
          <CardHeader>
            <CardTitle>Share Your Experience</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div>
                <Label className="text-base font-medium">How would you rate your experience?</Label>
                <div className="mt-2 flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="p-1 transition-colors"
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      onClick={() => setRating(star)}
                    >
                      <Star
                        className={`h-8 w-8 ${
                          star <= (hoverRating || rating)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <p className="mt-1 text-sm text-gray-600">
                    {rating === 1 && "Poor"}
                    {rating === 2 && "Fair"}
                    {rating === 3 && "Good"}
                    {rating === 4 && "Very Good"}
                    {rating === 5 && "Excellent"}
                  </p>
                )}
              </div>

              {/* Client Name */}
              <div>
                <Label htmlFor="clientName">Your Name</Label>
                <Input
                  id="clientName"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              {/* Client Email */}
              <div>
                <Label htmlFor="clientEmail">Your Email</Label>
                <Input
                  id="clientEmail"
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                />
              </div>

              {/* Review Text */}
              <div>
                <Label htmlFor="reviewText">Your Review</Label>
                <Textarea
                  id="reviewText"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Tell us about your experience..."
                  rows={4}
                  className="resize-none"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  {reviewText.length}/2000 characters (minimum 10)
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="w-full"
                style={{
                  backgroundColor: validationResult.business?.primaryColor || undefined,
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting Review...
                  </>
                ) : (
                  "Submit Review"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Powered by ManageClean OS</p>
        </div>
      </div>
    </div>
  );
}
